import { QrCode } from "../control/ui/QrCode";
import { GoonlordsWordmark } from "../control/ui/Logo";
import { holdCountdown, useCountdown } from "../lib/useCountdown";

/**
 * The party badge: a QR in the corner of a television that hands the screen to whoever
 * points a camera at it.
 *
 * This is the QR that was DELETED from the pairing card, and it is back on a narrower
 * argument rather than in spite of the old one. The original reasoning still holds for
 * setting a screen up: scanning happens in the phone's camera app, so a QR is a path that
 * leads out of the app and back through a cold tab, and one mechanism the product owns end
 * to end beat two. What that case never had was a QUEUE — one operator, one television,
 * done once. At a party there is no operator to hand a code to, everybody is holding a
 * different phone, and "point your camera at the TV" is the only instruction that survives
 * being said across a room to the fifth person. So typing is still the default everywhere,
 * and this is switched on deliberately, per screen, by somebody standing at the glass.
 *
 * z-40 puts it in the tier `ReconnectingBadge` and `PausedBadge` already occupy — the
 * screen's own facts about itself — and bottom-right is the corner those two left free.
 *
 * It COVERS the fap crown of whatever region is under it (RegionFapOverlay pins its own at
 * bottom-3 right-3, a tier below). Accepted rather than worked around: the whole region is
 * the fap target, so the gesture survives everywhere else in the tile, and offsetting other
 * people's overlays around an opt-in badge is how a corner becomes a layout engine.
 *
 * `pointer-events-none` throughout. Nothing here is pressable — a QR is read by a camera,
 * not tapped — and a badge that swallowed clicks would be eating faps for the region it
 * sits on.
 *
 * THE QR STAYS DRAWN WHILE THE SCREEN IS HELD, with the countdown beside it. Hiding it
 * would save somebody one wasted scan and cost them the thing they actually want, which is
 * to be pointing their camera at it the second it frees.
 *
 * IT WEARS THE WORDMARK, the same one the site bar and the code card carry, and for the
 * same reason the card records: this is the app's face on a television somebody else set
 * up. A stranger at a party is being asked to point their camera at an unexplained square
 * in the corner of a screen playing porn — the mark is what says whose square it is, and
 * it is the difference between an invitation and a QR code on a wall.
 *
 * Sized by CSS rather than the component's `height` prop: the wordmark is fixed to the
 * QR's own clamped width so the two always agree, and the intrinsic width/height the
 * component still emits keeps the aspect ratio (and so the reserved space) correct while
 * the image loads.
 */
export function PartyBadge({
  url,
  heldUntil,
}: {
  /** The address the QR encodes — this screen's claim link. */
  url: string;
  /** A local deadline while somebody holds this screen, or null when it is free. */
  heldUntil: number | null;
}) {
  const left = useCountdown(heldUntil);
  const held = left > 0;
  return (
    <div className="pointer-events-none absolute bottom-3 right-3 z-40 flex flex-col items-center gap-1.5 rounded-xl border border-white/15 bg-black/70 p-2.5 backdrop-blur">
      <GoonlordsWordmark className="h-auto w-[clamp(4.5rem,7vw,9rem)]" />

      {/* The quiet zone is not decoration: a QR flush against a dark page is a QR that
          does not scan. QrCode draws its own white field, and this box keeps it square
          and padded whatever the badge sits on.

          BLOCKED in session replay (`ph-no-capture`, posthog-js's default blockClass —
          the analytics config does not override it): the QR encodes this screen's
          /claim/<code> link, a live pairing code, and the front door `/` that can wear
          this badge is a recorded route. The tip-1 MASK marker hides text, not an SVG
          path's `d`, so it cannot hide this; a blocked element replays as an empty box
          the same size. The caption below stays readable. */}
      <div className="ph-no-capture rounded-lg bg-white p-1.5">
        <QrCode text={url} className="h-auto w-[clamp(4.5rem,7vw,9rem)]" />
      </div>
      {/* Sized against the VIEWPORT rather than a breakpoint, like the code card: this is
          read from a sofa, and `sm:` cannot tell a laptop from a 55" television. */}
      <span
        className={
          held
            ? "text-[clamp(0.6875rem,1vw,1rem)] font-semibold tabular-nums text-amber-300"
            : "text-[clamp(0.6875rem,1vw,1rem)] font-medium text-zinc-300"
        }
      >
        {held ? `Taken · ${holdCountdown(left)}` : "Scan to control the screen"}
      </span>
    </div>
  );
}
